import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router';
import { api, isApiError, type DomainDetail } from '../../api';
import { DataError } from '../../components/DataError';
import { CustomerScope } from '../../components/NotifyCustomer';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { Loading } from '../../components/States';
import { useApi } from '../../lib/useApi';
import { DomainPageContext, type DomainPageData } from './context';
import { DomainAttention } from './DomainAttention';
import { DomainHeader, StatusRibbon, TabGrid } from './DomainHeader';
import { ActionRail, ContextRail } from './DomainRails';
import { DOMAIN_TABS } from './tabs';
import { domainTabUrl, placeFor, type DomainTabId } from './tabs/moved';

/**
 * `/domains/:name/:tab?` — the one page of a domain, in the owner's v2.1 spec: header with the
 * status ribbon, what needs attention (on every tab), the twelve tabs as a grid, and three
 * columns — who and what (left), the tab, every action, the locks and the renewal preconditions
 * (right).
 * Former tabs and views redirect to the tab (and the part of it) that shows them now.
 */
export function DomainDetailPage() {
  const { name = '' } = useParams();
  // A new domain starts from "loading": the previous domain's data must never be taken for it
  // (the canonical-URL redirect below would send the user back to the previous domain).
  return <DomainDetailBody key={name} name={name} />;
}

function DomainDetailBody({ name }: { name: string }) {
  const { t } = useTranslation('domains');
  const { tab = 'overview' } = useParams();
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  const result = useApi((signal) => api.getDomain(name, { signal }), [name]);
  const place = placeFor(tab, params.get('view'));
  useDocumentTitle(result.status === 'success' ? result.data.domain.unicode_name : name);

  const crumbs = [{ label: t('list.title'), to: '/domains' }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={name} crumbs={crumbs} />
        <Loading label={t('detail.loading')} rows={4} />
      </>
    );
  }

  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={name} crumbs={crumbs} />
        <DataError error={result.error} title={notFound ? t('detail.notFound') : t('detail.loadFailed')} onRetry={notFound ? undefined : result.reload} />
      </>
    );
  }

  const domain = result.data.domain;
  // Canonicalise UUID / Unicode / mixed-case URLs to the A-label, former tabs and views to the tab
  // (and section) that shows them now, unknown tabs to the overview. `action` is kept.
  if (domain.ascii_name !== name || !place || place.tab !== tab || params.has('view')) {
    const section = place?.section ?? (hash.slice(1) || undefined);
    return <Navigate to={domainTabUrl(domain.ascii_name, place?.tab ?? 'overview', section, params.get('action') ?? undefined)} replace />;
  }
  return <DomainWorkspace detail={result.data} tab={place.tab} reload={result.reload} />;
}

/** Brings `#section` into view once the tab's parts have loaded (they load on their own). */
function useScrollToSection(hash: string) {
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    if (!id) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el || ++tries > 20) window.clearInterval(timer);
      el?.scrollIntoView({ block: 'start' });
    }, 150);
    return () => window.clearInterval(timer);
  }, [hash]);
}

function DomainWorkspace({ detail, tab, reload }: { detail: DomainDetail; tab: DomainTabId; reload: () => void }) {
  const { hash } = useLocation();
  const d = detail.domain;
  // Shared by the ribbon, the attention banner and the action rail.
  const txs = useApi((signal) => api.listDomainRegistryTransactions(d.id, { signal }), [d.id, d.version]);
  const delegation = useApi((signal) => api.getDomainDelegation(d.id, { signal }), [d.id, d.version]);
  const registryState = useApi((signal) => api.getDomainRegistryState(d.id, { signal }), [d.id, d.version]);
  const contacts = useApi((signal) => api.getDomainContacts(d.id, { signal }), [d.id, d.version]);
  useScrollToSection(hash);
  const items = txs.status === 'success' ? txs.data.items : null;
  const shared: DomainPageData = {
    txs: items,
    registryState: registryState.status === 'success' ? registryState.data : null,
    delegation: delegation.status === 'success' ? delegation.data : null,
    delegationFailed: delegation.status === 'error' ? { error: delegation.error, retry: delegation.reload } : null,
    contacts: contacts.status === 'success' ? contacts.data : null,
  };
  const drifted = shared.contacts ? shared.contacts.items.filter((c) => !c.valid_until && (c.changed_fields ?? []).length > 0).length : 0;
  const Tab = DOMAIN_TABS[tab];
  const files = detail.dossier?.vnnic_files;
  const counts: Partial<Record<DomainTabId, string>> = files ? { dossier: `${files.required.length - files.missing.length}/${files.required.length}` } : {};
  return (
    <CustomerScope customerId={d.customer_id}>
      <DomainPageContext.Provider value={shared}>
      <DomainHeader detail={detail} tab={tab} />
      <StatusRibbon
        detail={detail}
        txs={txs.status === 'error' ? 'unavailable' : items}
        sync={registryState.status === 'success' ? registryState.data : registryState.status === 'error' ? 'unavailable' : null}
        dsCount={delegation.status === 'success' ? delegation.data.ds_records.length : delegation.status === 'error' ? 'unavailable' : null}
      />
      <DomainAttention detail={detail} txs={items} txFailed={txs.status === 'error'} contactDrift={drifted} onChanged={reload} tab={tab} />
      <TabGrid detail={detail} active={tab} counts={counts} />
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_216px] xl:grid-cols-[176px_minmax(0,1fr)_212px] 2xl:grid-cols-[212px_minmax(0,1fr)_248px] 2xl:gap-5">
        <div className="min-w-0 max-lg:hidden lg:col-span-2 xl:col-span-1">
          <ContextRail detail={detail} />
        </div>
        <div className="min-w-0 space-y-4" data-domain-tab={tab}>
          <Tab detail={detail} reload={reload} />
        </div>
        <ActionRail detail={detail} txs={items} onChanged={reload} />
      </div>
      </DomainPageContext.Provider>
    </CustomerScope>
  );
}
